/**
 * Lot Interaction Plugin for Native Interactive Site Map (ISM)
 *
 * Sole owner of lot polygon coloring and interaction: initial status colors, click/hover
 * selection, and chip-driven filter coloring (matched = status color, unmatched = grey).
 *
 * Deliberately separate from `renderCommunitySiteMap.plugin.js` (which only creates/attaches
 * the SVG overlay). This plugin reacts to `context.services.svgOverlayReady$` — emitted once
 * the overlay's SVG element exists — and to Redux store changes (selected lot, active chip).
 *
 * @module nativeInteractiveSiteMap/plugins/lotInteraction.plugin
 */
import { map, distinctUntilChanged } from 'rxjs/operators';
import createLotPolygonColors from '../components/lotPolygonColors.dom';
import createLotPolygonInteractionHandlers from '../handlers/lotPolygonInteractionHandlers';
import { selectSelectedLotId, selectLotStatusMap, selectMatchedLotIds } from '../redux/selectors';

export default function createLotInteractionPlugin() {
  const state = {
    lotColors: null,
    overlay: null,
    svgOverlaySubscription: null,
    filterSubscription: null,
  };

  /**
   * Re-applies lot coloring whenever the active chip/matched lots change. Set up once per
   * svgOverlayReady$ emission (needs state.lotColors, which is bound to that svgElement) and
   * torn down in stop().
   * @returns {import('rxjs').Subscription}
   */
  function setupSubscriptions(context) {
    function onMatchedLotIdsChanged(matchedSitePlanLotIds) {
      const currentState = context.services.getState();
      state.lotColors?.applyFilter(selectLotStatusMap(currentState), matchedSitePlanLotIds);
    }

    return context.services.store$.pipe(
      map((storeState) => selectMatchedLotIds(storeState).sitePlanLotIds),
      distinctUntilChanged(),
    ).subscribe(onMatchedLotIdsChanged);
  }

  return {
    name: 'lot-interaction',

    async start(context) {
      function onSvgOverlayReady({ overlay, svgElement }) {
        state.overlay = overlay;

        function onLotSelected(lotId) {
          const prevLotId = selectSelectedLotId(context.services.getState());
          context.services.dispatch(context.services.dataActions.lotSelected(lotId));
          state.lotColors?.setSelected(prevLotId, lotId);
        }

        function onLotHovered(lotId) {
          state.lotColors?.setHovered(lotId);
        }

        function onLotLeft() {
          state.lotColors?.clearHovered();
        }

        const handlers = createLotPolygonInteractionHandlers({ onLotSelected, onLotHovered, onLotLeft });
        handlers.onSvgReady(svgElement);

        state.lotColors = createLotPolygonColors(svgElement);

        const initialState = context.services.getState();
        state.lotColors.applyFilter(selectLotStatusMap(initialState), selectMatchedLotIds(initialState).sitePlanLotIds);

        overlay.bindEvents({
          onLotClick: ({ lotId }) => handlers.onLotClick({ lotId }),
          onLotHover: ({ lotId }) => handlers.onLotHover({ lotId }),
          onLotEnter: (args) => handlers.onLotEnter(args),
          onLotLeave: (args) => handlers.onLotLeave(args),
        });

        state.filterSubscription?.unsubscribe?.();
        state.filterSubscription = setupSubscriptions(context);
      }

      state.svgOverlaySubscription = context.services.svgOverlayReady$.subscribe(onSvgOverlayReady);
    },

    async stop() {
      state.svgOverlaySubscription?.unsubscribe?.();
      state.svgOverlaySubscription = null;

      state.filterSubscription?.unsubscribe?.();
      state.filterSubscription = null;

      // Unbind our own DOM listeners now, instead of relying on renderCommunitySiteMap.plugin
      // detaching the overlay later (pluginHost stops plugins in reverse registration order,
      // so this plugin stops before that one — see PR #6518 review).
      state.overlay?.bindEvents();
      state.overlay = null;

      state.lotColors = null;
    },
  };
}
